import { useEffect, useRef, useState } from "react";
import { reviewApi, type ReviewItem } from "../../lib/review-api";
import { ReviewBadge } from "./reviewShared";
import { fmtDate, shortId } from "./adminShared";
import { cn } from "../cn";

const FILTERS: { key: string; label: string }[] = [
  { key: "unreviewed", label: "Pending" },
  { key: "new", label: "New" },
  { key: "republish", label: "Republish" },
  { key: "reviewed", label: "Reviewed" },
  { key: "", label: "All" },
];

/**
 * CENTER pane — the operational Review Queue. Discovers NEW / REPUBLISH public
 * publications without manual searching. Selection drives the Inspector. No
 * mutation happens here.
 */
export function ReviewQueue({
  tick,
  selectedId,
  onSelect,
  onList,
}: {
  tick: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onList: (ids: string[]) => void;
}) {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("unreviewed");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const reqRef = useRef(0);

  useEffect(() => {
    const my = ++reqRef.current;
    const c = new AbortController();
    setLoading(true);
    reviewApi
      .queue({ filter: filter || undefined, sort, limit: 100 }, c.signal)
      .then((res) => {
        if (my !== reqRef.current) return;
        setItems(res.items);
        setLoading(false);
      })
      .catch(() => {
        if (c.signal.aborted || my !== reqRef.current) return;
        setItems([]);
        setLoading(false);
      });
    return () => c.abort();
  }, [filter, sort, tick]);

  useEffect(() => {
    onList(items.map((i) => i.identity_id));
  }, [items, onList]);

  return (
    <div data-testid="review-queue" className="panel-chamfer panel-chamfer--sm overflow-hidden">
      <div className="chamfer-strip">
        <span className="strip-dot" aria-hidden />
        <span>Review Queue</span>
        <span className="ml-auto font-mono text-[9px] tracking-[0.14em] text-text-muted">
          {loading ? "…" : items.length}
        </span>
      </div>

      <div className="px-3 py-2.5 border-b border-white/5 flex flex-wrap items-center gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            data-testid={`review-filter-${f.key || "all"}`}
            data-active={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={cn("facet-btn h-7 px-2.5 text-[10px] tracking-[0.12em]", filter === f.key && "facet-btn--active")}
          >
            {f.label}
          </button>
        ))}
        <span aria-hidden className="mx-0.5 h-4 w-px bg-white/10" />
        <button
          type="button"
          data-testid="review-sort-toggle"
          onClick={() => setSort((s) => (s === "newest" ? "oldest" : "newest"))}
          className="facet-btn h-7 px-2.5 text-[10px] tracking-[0.12em]"
        >
          {sort === "newest" ? "Newest" : "Oldest"}
        </button>
      </div>

      <div className="max-h-[64vh] overflow-y-auto scroll-dark">
        {loading && (
          <div data-testid="review-queue-loading" className="px-4 py-6 text-center font-mono text-[11px] text-text-muted">
            Loading queue…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div data-testid="review-queue-empty" className="px-4 py-8 text-center font-mono text-[11px] text-text-muted">
            Nothing matches this view.
          </div>
        )}
        <ul className="divide-y divide-white/5">
          {items.map((it) => (
            <li key={it.identity_id}>
              <button
                type="button"
                data-testid={`review-queue-row-${it.identity_id}`}
                data-selected={selectedId === it.identity_id}
                onClick={() => onSelect(it.identity_id)}
                className={cn(
                  "w-full text-left px-4 py-3 transition-colors",
                  selectedId === it.identity_id ? "bg-[color:var(--spec-cyan-soft)]" : "hover:bg-white/[0.03]",
                )}
              >
                <div className="flex items-center gap-2">
                  <ReviewBadge classification={it.classification} testid="review-queue-row-badge" />
                  {it.moderated && (
                    <span data-testid="review-queue-row-moderated" className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-neon-coral">
                      hidden
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[9px] text-text-muted">{fmtDate(it.current_published_at)}</span>
                </div>
                <div className="mt-1.5 text-[12px] text-text-primary truncate">
                  {it.display_name || <span className="font-mono text-[11px] text-text-secondary">{shortId(it.identity_id, 12, 8)}</span>}
                </div>
                <div className="mt-0.5 flex items-center gap-3 font-mono text-[9.5px] text-text-muted">
                  <span>rev {it.current_publication_revision}</span>
                  <span>reviewed {it.last_reviewed_revision ?? "—"}</span>
                  <span className="truncate">{shortId(it.owner, 8, 6)}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
